/**
 * ABR (Adaptive Bitrate) state machine — client-side orchestration layer.
 *
 * Responsibilities:
 *  1. Track consecutive QoS poll results and apply escalation/restore rules.
 *  2. Maintain smoothed throughput estimate (EMA + harmonic mean) for rendition prediction.
 *  3. Produce ABR actions that are forwarded to the signaling server as qos_report context.
 *     SDK does NOT directly issue quality_hint — server is the final writer.
 *  4. Expose ClassifyContext to QosController so thresholds are context-aware.
 *
 * Design reference: docs/qos/design-qos.md §ABR State Machine
 *
 * WebRTC multi-level escalation:
 *   L1 (no downtime): reduce AVBR max bitrate within current rendition
 *   L2 (no downtime): reduce FPS 15→10→5
 *   L3 (~500ms-2s):   switch rendition (resolution change)
 *
 * JT/T1078 binary:
 *   primary (HD) ←→ secondary (SD)
 *
 * Hysteresis (degrade fast / restore slow):
 *   L1/L2 degrade: degraded×2 or poor×2 → restore: good×8
 *   L3    degrade: poor×4                → restore: good×10 + throughput check
 *   JT    degrade: poor×2                → restore: good×8
 *
 * ASSUMPTION: SDK does not directly apply quality changes. AbrController computes
 * the desired action and sets it on the pending action queue; the session layer
 * includes abr_hint in qos_report so the server can act on it. The server issues
 * the actual quality_hint to the streamer/firmware.
 */

import type { CameraCapability, CameraRendition, ClassifyContext, TransportMetrics } from '../runtime/types';
import type { QualityChangeParams } from '../transport/types';

// ── Hysteresis constants ──────────────────────────────────────────────────────
// Basis: Apple HLS Authoring Spec recommends restore 3-4x slower than degrade.
// SIGCOMM 2015 (Huang et al.) safety margin 0.85, harmonic mean for conservative prediction.

const DEGRADE_L1_THRESHOLD = 2;   // degraded×2 polls → L1
const DEGRADE_L2_THRESHOLD = 6;   // poor×6 polls     → L2 (fps reduce) — raised to slow down fps changes
const DEGRADE_L3_THRESHOLD = 4;   // poor×4 polls     → L3 (rendition switch)
const RESTORE_L1L2_THRESHOLD = 15; // good×15 polls    → restore L1/L2 — raised for stability
const RESTORE_L3_THRESHOLD = 10;  // good×10 + throughput check → restore L3
const DEGRADE_JT_THRESHOLD = 2;   // poor×2 → secondary
const RESTORE_JT_THRESHOLD = 8;   // good×8 → primary

const RENDITION_SWITCH_COOLDOWN_POLLS = 10; // ~30s at 3s poll interval
// Emergency RTT threshold: when RTT exceeds this, immediately halve bitrate
// regardless of L1/L2 counter thresholds. Prevents ICE failure under severe
// bufferbloat (e.g. throttled link with multiple concurrent streams).
const EMERGENCY_RTT_MS = 1500;

// ── Throughput estimation ─────────────────────────────────────────────────────
const EMA_ALPHA = 0.1;        // heavy smoothing; O(1) memory/compute
const HARMONIC_WINDOW = 5;    // last 5 EMA samples for harmonic mean
const SAFETY_MARGIN = 0.85;   // Netflix/Apple ABR safety margin

// ── FPS levels (WebRTC) ───────────────────────────────────────────────────────
const FPS_STEPS = [15, 10, 5] as const;

// ── ABR level ────────────────────────────────────────────────────────────────
export type AbrLevel = 0 | 1 | 2 | 3; // 0=best, 3=lowest/tier-switch

// ── ABR action (reported to server via qos_report.abr_hint) ──────────────────
export interface AbrAction {
  action: QualityChangeParams['action'];
  rendition?: string;
  bitrateKbps?: number;
  fps?: number;
  rcMode?: 'AVBR';
  avbrMinKbps?: number;
  avbrMaxKbps?: number;
  tier?: 'primary' | 'secondary'; // JT/T1078
  reason: 'congestion' | 'recovery';
  level: AbrLevel;
  hasDowntime: boolean;
}

// ── ABR state (serialisable for debug) ───────────────────────────────────────
export interface AbrState {
  /** Current escalation level (WebRTC) or JT1078 tier. */
  level: AbrLevel;
  /** JT/T1078 tier: 'primary' | 'secondary'. Only meaningful for stream_socket. */
  jt1078Tier: 'primary' | 'secondary';
  /** Currently active rendition label (WebRTC). Undefined until capability received. */
  renditionLabel: string | undefined;
  /** Current FPS target (WebRTC L2). */
  fpsTarget: number;
  /** Current AVBR max bitrate target (WebRTC L1). */
  bitrateMaxKbps: number;
  /** Smoothed throughput (EMA). */
  smoothedThroughputKbps: number;
  /** Consecutive degraded poll count. */
  degradedCount: number;
  /** Consecutive poor poll count. */
  poorCount: number;
  /** Consecutive good poll count. */
  goodCount: number;
  /** Polls since last rendition switch (cooldown). */
  pollsSinceSwitch: number;
  /** Most recent RTT (ms) — set in tick() for emergency degrade path. */
  lastRttMs: number;
}

/**
 * AbrController — per-session ABR state machine.
 *
 * Usage:
 *   const abr = new AbrController('interactive', capability);
 *   // each poll:
 *   const ctx = abr.getClassifyContext();
 *   const quality = qos.classify(metrics, ctx);
 *   const action = abr.tick(quality, metrics); // may return undefined if no action needed
 *   if (action) { include action in qos_report.abr_hint; }
 */
export class AbrController {
  private state: AbrState;
  private readonly family: 'interactive' | 'stream_socket';
  private capability: CameraCapability | undefined;
  private readonly throughputHistory: number[] = [];

  constructor(family: 'interactive' | 'stream_socket', capability?: CameraCapability) {
    this.family = family;
    this.capability = capability;

    const defaultRendition = capability?.renditions[0];
    this.state = {
      level: 0,
      jt1078Tier: 'primary',
      renditionLabel: defaultRendition?.label,
      fpsTarget: capability?.nominalFps ?? 15,
      bitrateMaxKbps: defaultRendition?.bitrateMaxKbps ?? 4000,
      smoothedThroughputKbps: 0,
      degradedCount: 0,
      poorCount: 0,
      goodCount: 0,
      pollsSinceSwitch: RENDITION_SWITCH_COOLDOWN_POLLS, // start ready
      lastRttMs: 0,
    };
  }

  /** Update capability when capability_report arrives. */
  updateCapability(cap: CameraCapability): void {
    this.capability = cap;
    if (this.state.renditionLabel === undefined && cap.renditions.length > 0) {
      this.state.renditionLabel = cap.renditions[0].label;
      this.state.bitrateMaxKbps = cap.renditions[0].bitrateMaxKbps;
      this.state.fpsTarget = cap.nominalFps;
    }
  }

  /** Build ClassifyContext from current ABR state for QosController. */
  getClassifyContext(): ClassifyContext {
    return {
      nominalFps: this.capability?.nominalFps ?? 15,
      hasRtt: this.family === 'interactive',
      targetBitrateKbps: this.state.bitrateMaxKbps,
      smoothedThroughputKbps: this.state.smoothedThroughputKbps,
    };
  }

  /** Get current ABR state (for debug snapshot). */
  getState(): Readonly<AbrState> {
    return { ...this.state };
  }

  /**
   * Advance the ABR state machine by one poll tick.
   *
   * @param quality  Result of QosController.classify() for this poll.
   * @param metrics  Raw metrics (used for throughput update).
   * @returns AbrAction if an adaptation should be requested, or undefined if no change.
   *
   * The returned action is an *intent* — the session layer includes it as `abr_hint`
   * in the qos_report uplink. The signaling server is the final decision authority.
   * SDK must NOT apply quality changes directly.
   */
  tick(quality: 'good' | 'degraded' | 'poor', metrics: TransportMetrics): AbrAction | undefined {
    this.updateThroughput(metrics);
    this.state.pollsSinceSwitch = Math.min(this.state.pollsSinceSwitch + 1, RENDITION_SWITCH_COOLDOWN_POLLS + 1);

    if (quality === 'good') {
      this.state.degradedCount = 0;
      this.state.poorCount = 0;
      this.state.goodCount++;
    } else if (quality === 'degraded') {
      this.state.goodCount = 0;
      this.state.degradedCount++;
      this.state.poorCount = 0;
    } else {
      this.state.goodCount = 0;
      this.state.poorCount++;
      this.state.degradedCount++;
    }

    this.state.lastRttMs = metrics.rttMs ?? 0;

    if (this.family === 'stream_socket') {
      return this.tickJt1078(quality);
    }
    return this.tickWebRtc(quality);
  }

  // ── JT/T1078 binary state machine ──────────────────────────────────────────

  private tickJt1078(quality: 'good' | 'degraded' | 'poor'): AbrAction | undefined {
    const s = this.state;

    if (s.jt1078Tier === 'primary' && s.poorCount >= DEGRADE_JT_THRESHOLD) {
      s.jt1078Tier = 'secondary';
      s.goodCount = 0;
      return {
        action: 'adjust_params',
        tier: 'secondary',
        reason: 'congestion',
        level: 3,
        hasDowntime: false,
      };
    }

    if (s.jt1078Tier === 'secondary' && s.goodCount >= RESTORE_JT_THRESHOLD) {
      s.jt1078Tier = 'primary';
      s.poorCount = 0;
      s.degradedCount = 0;
      return {
        action: 'adjust_params',
        tier: 'primary',
        reason: 'recovery',
        level: 3,
        hasDowntime: false,
      };
    }

    return undefined;
  }

  // ── WebRTC multi-level state machine ─────────────────────────────────────

  private tickWebRtc(quality: 'good' | 'degraded' | 'poor'): AbrAction | undefined {
    const s = this.state;
    const cap = this.capability;
    const currentRendition = this.currentRendition();

    // ── Degrade path ─────────────────────────────────────────────────────────

    // Emergency L0: when RTT > EMERGENCY_RTT_MS, immediately halve bitrate
    // without waiting for L1/L2 counter thresholds. Fires on every poor/degraded
    // poll until bitrate reaches the rendition minimum. This prevents ICE failure
    // under severe bufferbloat caused by multiple streams on a throttled link.
    if (quality !== 'good' && s.lastRttMs > EMERGENCY_RTT_MS) {
      const minBitrate = currentRendition?.bitrateMinKbps ?? 150;
      const emergency = Math.max(Math.round(s.bitrateMaxKbps * 0.5), minBitrate);
      if (emergency < s.bitrateMaxKbps) {
        s.bitrateMaxKbps = emergency;
        s.level = Math.max(s.level, 1) as AbrLevel;
        s.goodCount = 0;
        return this.buildAdjustAction('congestion', 1);
      }
    }

    // L3 (rendition switch) is DISABLED: resolution is fixed per channel.
    // Users select HD/SD channel explicitly; ABR only adjusts bitrate and fps.

    // L2: poor×6 → reduce fps
    if (s.poorCount >= DEGRADE_L2_THRESHOLD && s.level < 2) {
      const nextFps = this.lowerFps(s.fpsTarget);
      if (nextFps !== null) {
        s.level = Math.max(s.level, 2) as AbrLevel;
        s.fpsTarget = nextFps;
        s.goodCount = 0;
        return this.buildAdjustAction('congestion', 2);
      }
    }

    // L1: degraded×2 → reduce AVBR max bitrate
    if (s.degradedCount >= DEGRADE_L1_THRESHOLD && s.level < 1) {
      const newBitrate = Math.max(
        Math.round(s.bitrateMaxKbps * 0.75),
        currentRendition?.bitrateMinKbps ?? 150
      );
      if (newBitrate < s.bitrateMaxKbps) {
        s.level = Math.max(s.level, 1) as AbrLevel;
        s.bitrateMaxKbps = this.clampToRecordingSafeFloor(newBitrate, currentRendition);
        s.goodCount = 0;
        return this.buildAdjustAction('congestion', 1);
      }
    }

    // ── Restore path ─────────────────────────────────────────────────────────

    // Restore L3 (rendition switch) is DISABLED: resolution is fixed per channel.

    // Restore L1/L2: good×8 → increase bitrate and fps
    if (s.goodCount >= RESTORE_L1L2_THRESHOLD && s.level > 0 && s.level < 3) {
      let acted = false;

      // Restore fps first (L2)
      const nominalFps = cap?.nominalFps ?? 15;
      if (s.fpsTarget < nominalFps) {
        s.fpsTarget = this.higherFps(s.fpsTarget, nominalFps);
        s.level = s.fpsTarget < nominalFps ? 2 : 1;
        acted = true;
      }

      // Restore bitrate (L1) — increase by 25% toward rendition max
      const rendMax = currentRendition?.bitrateMaxKbps ?? s.bitrateMaxKbps;
      if (s.bitrateMaxKbps < rendMax) {
        const restored = Math.min(Math.round(s.bitrateMaxKbps * 1.25), rendMax);
        s.bitrateMaxKbps = restored;
        if (s.bitrateMaxKbps >= rendMax) s.level = Math.min(s.level, 1) as AbrLevel;
        acted = true;
      }

      if (acted) {
        if (s.bitrateMaxKbps >= rendMax && s.fpsTarget >= (cap?.nominalFps ?? 15)) {
          s.level = 0;
        }
        s.poorCount = 0;
        s.degradedCount = 0;
        return this.buildAdjustAction('recovery', s.level === 0 ? 1 : s.level as AbrLevel);
      }
    }

    return undefined;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private updateThroughput(metrics: TransportMetrics): void {
    const sample = metrics.availableIncomingBitrateKbps;
    if (sample === undefined || sample <= 0) return;

    if (this.state.smoothedThroughputKbps === 0) {
      this.state.smoothedThroughputKbps = sample;
    } else {
      this.state.smoothedThroughputKbps =
        EMA_ALPHA * sample + (1 - EMA_ALPHA) * this.state.smoothedThroughputKbps;
    }

    this.throughputHistory.push(this.state.smoothedThroughputKbps);
    if (this.throughputHistory.length > HARMONIC_WINDOW) {
      this.throughputHistory.shift();
    }
  }

  /**
   * Conservative throughput prediction using harmonic mean of last N EMA samples.
   * Harmonic mean penalises low values → appropriate for burst mobile links.
   * Safety margin 0.85 applied (Netflix/Apple HLS standard).
   */
  private predictedThroughputKbps(): number {
    if (this.throughputHistory.length === 0) return 0;
    const reciprocalSum = this.throughputHistory.reduce((acc, v) => acc + (v > 0 ? 1 / v : 0), 0);
    const harmonicMean = this.throughputHistory.length / reciprocalSum;
    return harmonicMean * SAFETY_MARGIN;
  }

  private currentRendition(): CameraRendition | undefined {
    if (!this.capability || !this.state.renditionLabel) return undefined;
    return this.capability.renditions.find(r => r.label === this.state.renditionLabel);
  }

  /** Select the next lower rendition than current. */
  private selectLowerRendition(): CameraRendition | undefined {
    if (!this.capability) return undefined;
    const rends = this.capability.renditions; // ordered high→low
    const idx = rends.findIndex(r => r.label === this.state.renditionLabel);
    if (idx < 0 || idx >= rends.length - 1) return undefined;
    return rends[idx + 1];
  }

  /** Select highest rendition that fits within predicted throughput. */
  private selectHigherRendition(predictedKbps: number): CameraRendition | undefined {
    if (!this.capability || predictedKbps <= 0) return undefined;
    const rends = this.capability.renditions; // high→low
    const idx = rends.findIndex(r => r.label === this.state.renditionLabel);
    if (idx <= 0) return undefined; // already at highest

    // Find highest rendition above current where bitrateMax fits predicted throughput
    for (let i = 0; i < idx; i++) {
      if (rends[i].bitrateMaxKbps <= predictedKbps) {
        return rends[i];
      }
    }
    return undefined;
  }

  private lowerFps(current: number): number | null {
    const idx = FPS_STEPS.indexOf(current as typeof FPS_STEPS[number]);
    if (idx < 0) {
      // Snap to nearest step below
      const below = [...FPS_STEPS].reverse().find(f => f < current);
      return below ?? null;
    }
    if (idx >= FPS_STEPS.length - 1) return null; // already at minimum
    return FPS_STEPS[idx + 1];
  }

  private higherFps(current: number, max: number): number {
    const step = [...FPS_STEPS].find(f => f > current && f <= max);
    return step ?? Math.min(current, max);
  }

  /**
   * Clamp bitrate to recording-safe floor when rendition is shared with recording.
   * ABR MUST NOT go below recordingSafeMinBitrateKbps for shared-encoder renditions.
   */
  private clampToRecordingSafeFloor(bitrateKbps: number, rendition: CameraRendition | undefined): number {
    if (!rendition?.sharedFromRecording) return bitrateKbps;
    const floor = rendition.recordingSafeMinBitrateKbps;
    if (floor === undefined) return bitrateKbps;
    return Math.max(bitrateKbps, floor);
  }

  private buildAdjustAction(reason: 'congestion' | 'recovery', level: AbrLevel): AbrAction {
    const rendition = this.currentRendition();
    return {
      action: 'adjust_params',
      rendition: this.state.renditionLabel,
      bitrateKbps: this.state.bitrateMaxKbps,
      fps: this.state.fpsTarget,
      rcMode: 'AVBR',
      avbrMinKbps: rendition?.bitrateMinKbps,
      avbrMaxKbps: this.state.bitrateMaxKbps,
      reason,
      level,
      hasDowntime: false,
    };
  }
}
